import endpoints from '@app/src/api/endpoints'
import { FetchKey, useFetchCollectionWithPost } from '@app/src/api/fetchHooks'
import Permissions, { usePermissions } from '@app/src/auth/permissions'
import { useCreationModalProgress } from '@app/src/context/CreationModalProgressContext'
import { useFetchAssessmentSettings } from '@app/src/pages/Assessments/useFetchAssessmentSettings'
import { RequestAutomationType } from '@app/src/pages/Configurations/ConfigurationsPages/Automation/RequestAutomationType'
import { Operators } from '@app/src/pages/ResourceCollection/Filters/useFilters'
import type { RequestAutomation } from '@app/src/types/automations'
import useAiScreeningEnabled from '@app/src/hooks/useAiScreeningEnabled'
import {
  AssessmentOutlined,
  CategoryOutlined,
  DoneAll,
  LabelOutlined,
  PublishedWithChangesOutlined,
  Search,
} from '@mui/icons-material'
import { Grid } from '@mui/material'
import { useFlags } from 'launchdarkly-react-client-sdk'
import React, { useCallback, useMemo } from 'react'
import { useFormContext } from 'react-hook-form'
import { useIntl } from 'react-intl'
import { computeAutomationVisibility, filterEnabledAssessments } from '../automationUtils'
import { AUTOMATION_QUESTION_KEYS } from '../CreateAutomationModal'
import AutomationTypeCard from './AutomationTypeCard'

// Orchestrates data fetching and calls pure logic
const AutomationType: React.FC = () => {
  const { formatMessage } = useIntl()
  const { setActiveStep } = useCreationModalProgress()
  const { setValue } = useFormContext()
  const { baselineExpectation: showBaselineExpectation, multiAssessmentExpectation } = useFlags()
  const isAiScreeningEnabled = useAiScreeningEnabled()

  const { hasPermission } = usePermissions()
  const { assessmentSettings } = useFetchAssessmentSettings()

  // Fetch existing automations to check if AI Enrich already exists
  const { items: existingAutomations } = useFetchCollectionWithPost<RequestAutomation>({
    key: FetchKey.Automation,
    endpoint: endpoints.automations,
    payload: {
      filter: [
        {
          name: AUTOMATION_QUESTION_KEYS.AUTOMATION_TYPE,
          filters: [{ value: RequestAutomationType.AI_ENRICH, operator: Operators.EqualTo }],
        },
      ],
      include: [],
    },
  })

  // Computation with memoization
  const visibility = useMemo(
    () =>
      computeAutomationVisibility(
        isAiScreeningEnabled,
        hasPermission(Permissions.ASSESSMENT_TEMPLATE_ACCESS),
        Boolean(filterEnabledAssessments(assessmentSettings).length),
        existingAutomations,
      ),
    [isAiScreeningEnabled, hasPermission, assessmentSettings, existingAutomations],
  )

  const onTypeSelect = useCallback(
    (requestAutomationType: RequestAutomationType) => {
      setActiveStep(currentValue => currentValue + 1)
      setValue(AUTOMATION_QUESTION_KEYS.AUTOMATION_TYPE, requestAutomationType)
    },
    [setActiveStep, setValue],
  )

  return (
    <Grid container spacing={3} width="100%">
      <Grid item xs={12} sm={6} md={4}>
        <AutomationTypeCard
          onClick={() => onTypeSelect(RequestAutomationType.FOR_EVERY_NEW_PROVIDER)}
          title={formatMessage({ id: 'form.createAutomation.automationType.newCompany' })}
          icon={DoneAll}
          description={formatMessage({ id: 'form.createAutomation.automationType.newCompanyDescription' })}
        />
      </Grid>
      <Grid item xs={12} sm={6} md={4}>
        <AutomationTypeCard
          onClick={() => onTypeSelect(RequestAutomationType.FOR_PROVIDERS_IN_CATEGORY)}
          title={formatMessage({ id: 'form.createAutomation.automationType.category' })}
          icon={LabelOutlined}
          description={formatMessage({ id: 'form.createAutomation.automationType.categoryDescription' })}
        />
      </Grid>
      {visibility.showAssessmentLevel && (
        <Grid item xs={12} sm={6} md={4}>
          <AutomationTypeCard
            onClick={() => onTypeSelect(RequestAutomationType.FOR_PROVIDERS_WITH_NEW_ASSESSMENT_LEVEL)}
            title={formatMessage({ id: 'form.createAutomation.automationType.providersWithNewAssessmentLevel' })}
            icon={AssessmentOutlined}
            description={formatMessage({
              id: 'form.createAutomation.automationType.providersWithNewAssessmentLevelDescription',
            })}
          />
        </Grid>
      )}
      {visibility.showAiScreen && (
        <Grid item xs={12} sm={6} md={4}>
          <AutomationTypeCard
            onClick={() => onTypeSelect(RequestAutomationType.AI_SCREEN)}
            title={formatMessage({ id: 'form.createAutomation.automationType.aiScreen' })}
            icon={Search}
            description={formatMessage({ id: 'form.createAutomation.automationType.aiScreenDescription' })}
            variant="ai"
          />
        </Grid>
      )}
      {visibility.showAiEnrich && (
        <Grid item xs={12} sm={6} md={4}>
          <AutomationTypeCard
            onClick={() => onTypeSelect(RequestAutomationType.AI_ENRICH)}
            title={formatMessage({ id: 'form.createAutomation.automationType.aiEnrich' })}
            icon={CategoryOutlined}
            description={formatMessage({ id: 'form.createAutomation.automationType.aiEnrichDescription' })}
            disabled={visibility.disableAiEnrich}
            disabledTooltip={formatMessage({ id: 'form.createAutomation.automationType.aiEnrichAlreadyExists' })}
            variant="ai"
          />
        </Grid>
      )}
      {showBaselineExpectation && (
        <Grid item xs={12} sm={6} md={4}>
          <AutomationTypeCard
            onClick={() => onTypeSelect(RequestAutomationType.EXPECTATION)}
            title={formatMessage({
              id: multiAssessmentExpectation
                ? 'form.createAutomation.summary.expectation.expectation'
                : 'form.createAutomation.automationType.baselineExpectation',
            })}
            icon={PublishedWithChangesOutlined}
            description={formatMessage({ id: 'form.createAutomation.automationType.baselineExpectationDescription' })}
            variant="expectation"
          />
        </Grid>
      )}
    </Grid>
  )
}
export default AutomationType
